import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api/api.client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, User, Mail, Building, Calendar, Shield, Hash } from 'lucide-react';

export function Profile() {
  const { user, isLoading: authLoading } = useAuth();
  
  // Map viewer role to client for display (backend returns 'viewer' for client users)
  const displayRole = user?.role === 'client' ? 'client' : user?.role;
  const isClient = user?.role === 'client';
  
  // Fetch client details if user has client_project_id
  const { data: clientData, isLoading: clientLoading } = useQuery({
    queryKey: ['client-details', user?.client_project_id],
    queryFn: async () => {
      if (!user?.client_project_id) return null;
      try {
        const response = await apiClient.get(`/clients/${user.client_project_id}`);
        return response.data?.data?.client;
      } catch (error) {
        console.error('Error fetching client details:', error);
        return null;
      }
    },
    enabled: !!user?.client_project_id,
    retry: 1,
  });

  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Format time for last access
  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-4xl">
        <h1 className="text-3xl font-bold mb-6">Mi Perfil</h1>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i}>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-6 w-64" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state - no user
  if (!user) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-4xl">
        <h1 className="text-3xl font-bold mb-6">Mi Perfil</h1>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No se pudo cargar la información del usuario. Por favor, intenta iniciar sesión nuevamente.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">Mi Perfil</h1>
      
      <div className="space-y-6">
        {/* Personal Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Información Personal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              {/* Name Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Nombre completo
                </label>
                <p className="text-lg font-medium">{user.name || 'No especificado'}</p>
              </div>
              
              {/* Email Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Correo electrónico
                </label>
                <p className="text-lg">{user.email}</p>
              </div>
              
              {/* Client/Company Field - Only for client users */}
              {isClient && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    Cliente asignado
                  </label>
                  <p className="text-lg">
                    {clientLoading ? (
                      <Skeleton className="h-6 w-32" />
                    ) : (
                      clientData?.name || user.company_name || 'No asignado'
                    )}
                  </p>
                  {clientData?.project_type && (
                    <p className="text-sm text-muted-foreground">
                      Tipo de proyecto: {clientData.project_type === 'chatbot' ? 'Chatbot' : clientData.project_type}
                    </p>
                  )}
                </div>
              )}
              
              {/* Role Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  Rol
                </label>
                <div>
                  <Badge variant={displayRole === 'admin' ? 'default' : displayRole === 'developer' ? 'secondary' : 'outline'}>
                    {displayRole === 'admin' ? 'Administrador' : 
                     displayRole === 'developer' ? 'Desarrollador' : 
                     'Cliente'}
                  </Badge>
                </div>
              </div>
              
              {/* Member Since Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Miembro desde
                </label>
                <p className="text-lg">{formatDate(user.created_at)}</p>
              </div>
              
              {/* Client Project ID - Only for client users */}
              {isClient && user.client_project_id && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    ID de proyecto
                  </label>
                  <p className="text-sm font-mono text-muted-foreground">
                    {user.client_project_id}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Account Information Card */}
        <Card>
          <CardHeader>
            <CardTitle>Información de Cuenta</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              {/* Account Status */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Estado de la cuenta
                </label>
                <div>
                  <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                    Activo
                  </Badge>
                </div>
              </div>
              
              {/* Profile Completion Status */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Perfil completo
                </label>
                <div>
                  <Badge variant={user.is_profile_complete ? 'outline' : 'secondary'} 
                         className={user.is_profile_complete ? 'bg-green-500/10 text-green-600 border-green-500/20' : ''}>
                    {user.is_profile_complete ? 'Completo' : 'Incompleto'}
                  </Badge>
                </div>
              </div>
              
              {/* Last Update */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Última actualización
                </label>
                <p className="text-lg">{formatDateTime(user.updated_at)}</p>
              </div>
              
              {/* Sessions */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Sesiones activas
                </label>
                <p className="text-lg">1</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Future Edit Mode Preparation - Hidden for now */}
        {/* This structure is prepared for future editing capabilities */}
        {false && !isClient && (
          <Card>
            <CardHeader>
              <CardTitle>Configuración adicional</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Las opciones de edición estarán disponibles próximamente.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default Profile;